import {
  TICKET_TYPE_LABELS,
  type TicketCategoryAdmin,
  type TicketTypeDefinition,
} from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  useCreateTicketCategory,
  useCreateTicketTypeDefinition,
  useDeleteTicketCategory,
  useDeleteTicketTypeDefinition,
  usePatchTicketCatalogSettings,
  useTicketCatalogAdmin,
  useUpdateTicketCategory,
  useUpdateTicketTypeDefinition,
} from '@/products/helpdesk/features/settings/use-ticket-catalog';
import { useTicketLookups } from '@/products/helpdesk/features/tickets/use-tickets';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';

const NONE = 'none';

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof ApiRequestError ? error.message : fallback;
}

function DeleteConfirm({
  title,
  description,
  busy,
  onConfirm,
}: {
  title: string;
  description: string;
  busy: boolean;
  onConfirm: () => Promise<void>;
}) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button type="button" size="sm" variant="outline" disabled={busy}>
          Löschen
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Abbrechen</AlertDialogCancel>
          <AlertDialogAction onClick={() => void onConfirm()}>Endgültig löschen</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function childrenOf(rows: TicketCategoryAdmin[], parentId: string | null): TicketCategoryAdmin[] {
  return rows
    .filter((row) => row.parentId === parentId)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'de'));
}

export function TicketCatalogPage() {
  usePageTitle('Katalog');
  const catalog = useTicketCatalogAdmin();
  const lookups = useTicketLookups();

  return (
    <section className="mx-auto max-w-5xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Katalog</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Kategorien und Ticketarten für Portal und Agenten. Ungenutzte Einträge können endgültig
          gelöscht werden.
        </p>
      </div>

      {catalog.isPending && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Katalog wird geladen …
        </p>
      )}

      {catalog.isError && (
        <p className="text-sm text-muted-foreground">
          {errorMessage(catalog.error, 'Der Katalog konnte nicht geladen werden.')}
        </p>
      )}

      {catalog.data && (
        <Tabs defaultValue="categories">
          <TabsList aria-label="Katalogbereiche">
            <TabsTrigger value="categories">Kategorien</TabsTrigger>
            <TabsTrigger value="types">Arten</TabsTrigger>
            <TabsTrigger value="portal">Portal</TabsTrigger>
          </TabsList>
          <TabsContent value="categories" forceMount className="data-[state=inactive]:hidden">
            <CategoriesTab
              categories={catalog.data.categories}
              queues={lookups.data?.queues ?? []}
              agents={lookups.data?.agents ?? []}
            />
          </TabsContent>
          <TabsContent value="types" forceMount className="data-[state=inactive]:hidden">
            <TypesTab types={catalog.data.typeDefinitions} />
          </TabsContent>
          <TabsContent value="portal" forceMount className="data-[state=inactive]:hidden">
            <PortalTab
              categories={catalog.data.categories}
              types={catalog.data.typeDefinitions}
              settings={catalog.data.settings}
            />
          </TabsContent>
        </Tabs>
      )}
    </section>
  );
}

function CategoriesTab({
  categories,
  queues,
  agents,
}: {
  categories: TicketCategoryAdmin[];
  queues: { id: string; name: string }[];
  agents: { id: string; displayName: string }[];
}) {
  const create = useCreateTicketCategory();
  const [dialog, setDialog] = useState<'group' | 'leaf' | null>(null);
  const roots = childrenOf(categories, null);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        <Button type="button" onClick={() => setDialog('group')}>
          Neue Gruppe
        </Button>
        <Button type="button" variant="outline" onClick={() => setDialog('leaf')}>
          Neues Blatt
        </Button>
      </div>
      {roots.length === 0 && (
        <p className="text-sm text-muted-foreground">Noch keine Kategorien vorhanden.</p>
      )}
      <div className="space-y-4">
        {roots.map((group) => (
          <CategoryGroupCard
            key={group.id}
            group={group}
            leaves={childrenOf(categories, group.id)}
            groups={roots}
            queues={queues}
            agents={agents}
          />
        ))}
      </div>
      <CategoryDialog
        kind={dialog}
        groups={roots}
        open={dialog !== null}
        busy={create.isPending}
        onOpenChange={(open) => {
          if (!open) {
            setDialog(null);
          }
        }}
        onSubmit={async (body) => {
          try {
            await create.mutateAsync(body);
            toast.success(dialog === 'group' ? 'Gruppe angelegt.' : 'Kategorie angelegt.');
            setDialog(null);
          } catch (error) {
            toast.error(errorMessage(error, 'Die Kategorie konnte nicht angelegt werden.'));
          }
        }}
      />
    </div>
  );
}

function CategoryGroupCard({
  group,
  leaves,
  groups,
  queues,
  agents,
}: {
  group: TicketCategoryAdmin;
  leaves: TicketCategoryAdmin[];
  groups: TicketCategoryAdmin[];
  queues: { id: string; name: string }[];
  agents: { id: string; displayName: string }[];
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <h2>{group.name}</h2>
        </CardTitle>
        <CardDescription>Gruppe · {group.slug}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <CategoryEditor
          row={group}
          isLeaf={leaves.length === 0}
          groups={groups}
          queues={queues}
          agents={agents}
        />
        {leaves.map((leaf) => (
          <div key={leaf.id} className="rounded-lg border p-4">
            <CategoryEditor row={leaf} isLeaf groups={groups} queues={queues} agents={agents} />
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function CategoryEditor({
  row,
  isLeaf,
  groups,
  queues,
  agents,
}: {
  row: TicketCategoryAdmin;
  isLeaf: boolean;
  groups: TicketCategoryAdmin[];
  queues: { id: string; name: string }[];
  agents: { id: string; displayName: string }[];
}) {
  const update = useUpdateTicketCategory();
  const remove = useDeleteTicketCategory();
  const [name, setName] = useState(row.name);
  const [keywords, setKeywords] = useState(row.keywords.join('\n'));

  async function patch(body: Parameters<typeof update.mutateAsync>[0]['body'], ok: string) {
    try {
      await update.mutateAsync({ id: row.id, body });
      toast.success(ok);
    } catch (error) {
      toast.error(errorMessage(error, 'Die Kategorie konnte nicht gespeichert werden.'));
    }
  }

  return (
    <div className="grid gap-4">
      <div className="grid gap-2 sm:grid-cols-[1fr_auto_auto] sm:items-end">
        <div className="space-y-1.5">
          <Label htmlFor={`cat-name-${row.id}`}>Name</Label>
          <Input
            id={`cat-name-${row.id}`}
            value={name}
            onChange={(event) => setName(event.target.value)}
            onBlur={() => {
              if (name.trim() && name.trim() !== row.name) {
                void patch({ name: name.trim() }, 'Name gespeichert.');
              }
            }}
          />
        </div>
        <div className="flex items-center gap-2">
          <Switch
            id={`cat-active-${row.id}`}
            checked={row.isActive}
            aria-label={`${row.name} aktiv`}
            onCheckedChange={(checked) =>
              void patch({ isActive: checked }, checked ? 'Aktiv.' : 'Deaktiviert.')
            }
          />
          <Label htmlFor={`cat-active-${row.id}`}>Aktiv</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch
            id={`cat-portal-${row.id}`}
            checked={row.portalVisible}
            aria-label={`${row.name} im Portal`}
            onCheckedChange={(checked) =>
              void patch(
                { portalVisible: checked },
                checked ? 'Im Portal sichtbar.' : 'Im Portal ausgeblendet.',
              )
            }
          />
          <Label htmlFor={`cat-portal-${row.id}`}>Im Portal</Label>
        </div>
      </div>
      {row.parentId && (
        <div className="space-y-1.5">
          <Label htmlFor={`cat-parent-${row.id}`}>Gruppe</Label>
          <Select
            value={row.parentId}
            onValueChange={(parentId) => void patch({ parentId }, 'Gruppe geändert.')}
          >
            <SelectTrigger id={`cat-parent-${row.id}`} className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {groups.map((item) => (
                <SelectItem key={item.id} value={item.id}>
                  {item.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="space-y-1.5">
        <Label htmlFor={`cat-assignee-${row.id}`}>Standard-Zuweisung</Label>
        <Select
          value={row.defaultAssigneeId ?? NONE}
          onValueChange={(value) =>
            void patch(
              { defaultAssigneeId: value === NONE ? null : value },
              'Zuweisung gespeichert.',
            )
          }
        >
          <SelectTrigger id={`cat-assignee-${row.id}`} className="w-full">
            <SelectValue placeholder="Keine" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>Keine</SelectItem>
            {agents.map((agent) => (
              <SelectItem key={agent.id} value={agent.id}>
                {agent.displayName}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          {isLeaf
            ? 'Neue Tickets in diesem Blatt gehen an diese Person. Leer: Person der Gruppe, sonst der Warteschlange.'
            : 'Gilt für Blätter ohne eigene Person.'}
        </p>
      </div>
      {isLeaf && (
        <>
          <div className="space-y-1.5">
            <Label htmlFor={`cat-queue-${row.id}`}>Standard-Warteschlange</Label>
            <Select
              value={row.defaultQueueId ?? NONE}
              onValueChange={(value) =>
                void patch(
                  { defaultQueueId: value === NONE ? null : value },
                  'Warteschlange gespeichert.',
                )
              }
            >
              <SelectTrigger id={`cat-queue-${row.id}`} className="w-full">
                <SelectValue placeholder="Keine" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Keine</SelectItem>
                {queues.map((queue) => (
                  <SelectItem key={queue.id} value={queue.id}>
                    {queue.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`cat-kw-${row.id}`}>Stichwörter (eine Phrase pro Zeile)</Label>
            <Textarea
              id={`cat-kw-${row.id}`}
              value={keywords}
              rows={4}
              onChange={(event) => setKeywords(event.target.value)}
            />
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() =>
                void patch({ keywords: keywords.split('\n') }, 'Stichwörter gespeichert.')
              }
            >
              Stichwörter speichern
            </Button>
          </div>
        </>
      )}
      <DeleteConfirm
        title={isLeaf ? 'Blatt löschen?' : 'Gruppe löschen?'}
        description={`„${row.name}“ wird endgültig entfernt. Tickets dürfen diese Kategorie nicht mehr verwenden.`}
        busy={remove.isPending}
        onConfirm={async () => {
          try {
            await remove.mutateAsync(row.id);
            toast.success(isLeaf ? 'Blatt gelöscht.' : 'Gruppe gelöscht.');
          } catch (error) {
            toast.error(errorMessage(error, 'Die Kategorie konnte nicht gelöscht werden.'));
          }
        }}
      />
    </div>
  );
}

function CategoryDialog({
  kind,
  groups,
  open,
  busy,
  onOpenChange,
  onSubmit,
}: {
  kind: 'group' | 'leaf' | null;
  groups: TicketCategoryAdmin[];
  open: boolean;
  busy: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (body: { name: string; parentId?: string | null }) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState(groups[0]?.id ?? NONE);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (next) {
          setName('');
          setParentId(groups[0]?.id ?? NONE);
        }
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{kind === 'group' ? 'Neue Gruppe' : 'Neues Blatt'}</DialogTitle>
          <DialogDescription>
            {kind === 'group'
              ? 'Gruppen ordnen Blätter. Tickets hängen nur an Blättern.'
              : 'Ein Blatt ist die Kategorie, die einem Ticket zugewiesen wird.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="new-category-name">Name</Label>
            <Input
              id="new-category-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          {kind === 'leaf' && (
            <div className="space-y-1.5">
              <Label htmlFor="new-category-parent">Gruppe</Label>
              <Select value={parentId} onValueChange={setParentId}>
                <SelectTrigger id="new-category-parent" className="w-full">
                  <SelectValue placeholder="Gruppe wählen" />
                </SelectTrigger>
                <SelectContent>
                  {groups.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button
            type="button"
            disabled={busy || name.trim().length < 2 || (kind === 'leaf' && parentId === NONE)}
            onClick={() =>
              void onSubmit({
                name: name.trim(),
                parentId: kind === 'leaf' ? parentId : null,
              })
            }
          >
            Anlegen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TypeCard({ row }: { row: TicketTypeDefinition }) {
  const update = useUpdateTicketTypeDefinition();
  const remove = useDeleteTicketTypeDefinition();
  const [name, setName] = useState(row.name);
  const [keywords, setKeywords] = useState(row.keywords.join('\n'));

  async function patch(body: Parameters<typeof update.mutateAsync>[0]['body'], ok: string) {
    try {
      await update.mutateAsync({ id: row.id, body });
      toast.success(ok);
    } catch (error) {
      toast.error(errorMessage(error, 'Die Ticketart konnte nicht gespeichert werden.'));
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          <h2 className="flex flex-wrap items-center gap-2">
            {row.name}
            {row.isSystem && <Badge variant="secondary">System</Badge>}
          </h2>
        </CardTitle>
        <CardDescription className="grid gap-3 sm:grid-cols-2">
          <span>
            <span className="block text-foreground">Kurzname</span>
            <span className="font-mono break-all">{row.slug}</span>
          </span>
          <span>
            <span className="block text-foreground">ITIL</span>
            {TICKET_TYPE_LABELS[row.itilType]}
          </span>
        </CardDescription>
        <CardAction>
          {row.isSystem ? (
            <span className="text-sm text-muted-foreground">Nicht löschbar</span>
          ) : (
            <DeleteConfirm
              title="Ticketart löschen?"
              description={`„${row.name}“ wird endgültig entfernt. Tickets dürfen diese Art nicht mehr verwenden.`}
              busy={remove.isPending}
              onConfirm={async () => {
                try {
                  await remove.mutateAsync(row.id);
                  toast.success('Ticketart gelöscht.');
                } catch (error) {
                  toast.error(errorMessage(error, 'Die Ticketart konnte nicht gelöscht werden.'));
                }
              }}
            />
          )}
        </CardAction>
      </CardHeader>
      <CardContent className="grid gap-4">
        <div className="grid gap-4 sm:grid-cols-[1fr_auto_auto] sm:items-end">
          <div className="space-y-1.5">
            <Label htmlFor={`type-name-${row.id}`}>Name</Label>
            <Input
              id={`type-name-${row.id}`}
              aria-label={`Name ${row.name}`}
              value={name}
              onChange={(event) => setName(event.target.value)}
              onBlur={() => {
                if (name.trim() && name.trim() !== row.name) {
                  void patch({ name: name.trim() }, 'Name gespeichert.');
                }
              }}
            />
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id={`type-active-${row.id}`}
              checked={row.isActive}
              aria-label={`${row.name} aktiv`}
              onCheckedChange={(checked) =>
                void patch({ isActive: checked }, checked ? 'Aktiv.' : 'Deaktiviert.')
              }
            />
            <Label htmlFor={`type-active-${row.id}`}>Aktiv</Label>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id={`type-portal-${row.id}`}
              checked={row.portalVisible}
              aria-label={`${row.name} im Portal`}
              onCheckedChange={(checked) =>
                void patch(
                  { portalVisible: checked },
                  checked ? 'Im Portal sichtbar.' : 'Im Portal ausgeblendet.',
                )
              }
            />
            <Label htmlFor={`type-portal-${row.id}`}>Im Portal</Label>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`type-kw-${row.id}`}>Stichwörter (eine Phrase pro Zeile)</Label>
          <Textarea
            id={`type-kw-${row.id}`}
            aria-label={`Stichwörter ${row.name}`}
            rows={4}
            value={keywords}
            onChange={(event) => setKeywords(event.target.value)}
          />
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() =>
              void patch({ keywords: keywords.split('\n') }, 'Stichwörter gespeichert.')
            }
          >
            Stichwörter speichern
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function TypesTab({ types }: { types: TicketTypeDefinition[] }) {
  const create = useCreateTicketTypeDefinition();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');

  return (
    <div className="space-y-4">
      <Button type="button" onClick={() => setOpen(true)}>
        Eigene Art
      </Button>
      {types.map((row) => (
        <TypeCard key={row.id} row={row} />
      ))}
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (next) {
            setName('');
          }
          setOpen(next);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eigene Art</DialogTitle>
            <DialogDescription>
              Eigene Arten verhalten sich intern wie eine Serviceanfrage. Problem und Change bleiben
              Systemarten.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="new-type-name">Name</Label>
            <Input
              id="new-type-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Abbrechen
            </Button>
            <Button
              type="button"
              disabled={create.isPending || name.trim().length < 2}
              onClick={async () => {
                try {
                  await create.mutateAsync({ name: name.trim() });
                  toast.success('Ticketart angelegt.');
                  setOpen(false);
                } catch (error) {
                  toast.error(errorMessage(error, 'Die Ticketart konnte nicht angelegt werden.'));
                }
              }}
            >
              Anlegen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PortalTab({
  categories,
  types,
  settings,
}: {
  categories: TicketCategoryAdmin[];
  types: TicketTypeDefinition[];
  settings: {
    portalCategoryPickerEnabled: boolean;
    portalTypePickerEnabled: boolean;
    defaultPortalCategoryId: string | null;
    defaultPortalTypeDefinitionId: string | null;
  };
}) {
  const patch = usePatchTicketCatalogSettings();
  const leaves = useMemo(
    () =>
      categories.filter(
        (row) =>
          row.isActive &&
          row.portalVisible &&
          categories.every((other) => other.parentId !== row.id),
      ),
    [categories],
  );
  const portalTypes = types.filter((row) => row.isActive && row.portalVisible);

  async function save(body: Parameters<typeof patch.mutateAsync>[0], ok: string) {
    try {
      await patch.mutateAsync(body);
      toast.success(ok);
    } catch (error) {
      toast.error(
        errorMessage(error, 'Die Portal-Einstellungen konnten nicht gespeichert werden.'),
      );
    }
  }

  return (
    <div className="grid gap-4">
      <Card>
        <CardHeader>
          <CardTitle>
            <h2 id="portal-category-title">Kategorie von Mitarbeitenden wählen lassen</h2>
          </CardTitle>
          <CardDescription>
            Aus: Das Portal zeigt kein Kategorie-Feld. Die IT ordnet anhand von Stichwörtern, KI und
            dem Standard zu.
          </CardDescription>
          <CardAction>
            <Switch
              checked={settings.portalCategoryPickerEnabled}
              aria-label="Kategorie von Mitarbeitenden wählen lassen"
              onCheckedChange={(checked) =>
                void save(
                  { portalCategoryPickerEnabled: checked },
                  checked
                    ? 'Kategorie-Auswahl im Portal eingeschaltet.'
                    : 'Kategorie-Auswahl im Portal ausgeschaltet.',
                )
              }
            />
          </CardAction>
        </CardHeader>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>
            <h2 id="portal-type-title">Art von Mitarbeitenden wählen lassen</h2>
          </CardTitle>
          <CardDescription>
            Aus: Das Portal zeigt keine Art. Störung oder Serviceanfrage ergibt sich aus
            Stichwörtern und dem Standard.
          </CardDescription>
          <CardAction>
            <Switch
              checked={settings.portalTypePickerEnabled}
              aria-label="Art von Mitarbeitenden wählen lassen"
              onCheckedChange={(checked) =>
                void save(
                  { portalTypePickerEnabled: checked },
                  checked
                    ? 'Art-Auswahl im Portal eingeschaltet.'
                    : 'Art-Auswahl im Portal ausgeschaltet.',
                )
              }
            />
          </CardAction>
        </CardHeader>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Standards</CardTitle>
          <CardDescription>
            Fallback, wenn weder Stichwort noch Nutzerwahl greifen. Die Standard-Kategorie muss ein
            aktives Portal-Blatt sein.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="default-category">Standard-Kategorie</Label>
            <Select
              value={settings.defaultPortalCategoryId ?? NONE}
              onValueChange={(value) =>
                void save(
                  { defaultPortalCategoryId: value === NONE ? null : value },
                  'Standard-Kategorie gespeichert.',
                )
              }
            >
              <SelectTrigger id="default-category" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Keine</SelectItem>
                {leaves.map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="default-type">Standard-Art</Label>
            <Select
              value={settings.defaultPortalTypeDefinitionId ?? NONE}
              onValueChange={(value) =>
                void save(
                  { defaultPortalTypeDefinitionId: value === NONE ? null : value },
                  'Standard-Art gespeichert.',
                )
              }
            >
              <SelectTrigger id="default-type" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Keine</SelectItem>
                {portalTypes.map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
